import './i18n/config';
import './config/axiosConfig';
import React, { useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import ToastContainer from './components/ToastContainer';

import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import UserFormPage from './pages/UserFormPage';
import PasswordResetPage from './pages/PasswordResetPage';
import ConfirmEmailPage from './pages/ConfirmEmailPage';
import ProductsPage from './pages/ProductsPage';
import DocumentsListPage from './pages/DocumentsListPage';
import MonetaryAccountsListPage from './pages/MonetaryAccountsListPage';
import MonetaryAccountFormPage from './pages/MonetaryAccountFormPage';
import MonetaryAccountDetailPage from './pages/MonetaryAccountDetailPage';
import ProductFormPage from './pages/ProductFormPage';
import ClientQuoteListPage from './pages/ClientQuoteListPage';
import ClientQuoteFormPage from './pages/ClientQuoteFormPage';
import ClientsListPage from './pages/ClientsListPage';
import ClientFormPage from './pages/ClientFormPage';
import SalesListPage from './pages/SalesListPage';
import SalesDetailPage from './pages/SalesDetailPage';
import SuppliersListPage from './pages/SuppliersListPage';
import SupplierFormPage from './pages/SupplierFormPage';
import PurchaseOrdersListPage from './pages/PurchaseOrdersListPage';
import PurchaseOrderFormPage from './pages/PurchaseOrderFormPage';
import PurchaseOrderDetailPage from './pages/PurchaseOrderDetailPage';
import EmployeesListPage from './pages/EmployeesListPage';
import PayrollListPage from './pages/PayrollListPage';
import PayrollFormPage from './pages/PayrollFormPage';
import PayrollDetailPage from './pages/PayrollDetailPage';
import NotFoundPage from './pages/NotFoundPage';
import EmployeeDetailPage from './pages/EmployeeDetailPage';
import EmployeeFormPage from './pages/EmployeeFormPage';

import Navigation from './components/Navigation';
import TopBar from './components/TopBar';
import PrivateRoute from './components/PrivateRoute';

const AppLayout = ({ children }) => {
  const { isAuthenticated } = useContext(AuthContext);

  if (!isAuthenticated) {
    return children;
  }

  return (
    <div className="app-shell">
      <Navigation />
      <TopBar />
      <main className="app-main">
        {children}
      </main>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <NotificationProvider>
        <ToastContainer />
        <AuthProvider>
          <AppLayout>
            <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/confirm-email" element={<ConfirmEmailPage />} />
            <Route path="/reset-password" element={<PasswordResetPage />} />

            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <DashboardPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/users"
              element={
                <PrivateRoute requireAdmin={true}>
                  <UsersPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/users/create"
              element={
                <PrivateRoute requireAdmin={true}>
                  <UserFormPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/users/:id/edit"
              element={
                <PrivateRoute requireAdmin={true}>
                  <UserFormPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/products"
              element={
                <PrivateRoute requiredPermission="products.access_products">
                  <ProductsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/files"
              element={
                <PrivateRoute requiredPermission="documents.access_documents">
                  <DocumentsListPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/monetary-accounts"
              element={
                <PrivateRoute requiredPermission="accounting.access_accounting">
                  <MonetaryAccountsListPage />
                </PrivateRoute>
              }
            />
            {/* Before /:id, or the literal "create" is captured as an id and
                the form loads a detail page for an account that does not
                exist. */}
            <Route
              path="/monetary-accounts/create"
              element={
                <PrivateRoute requiredPermission="accounting.access_accounting">
                  <MonetaryAccountFormPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/monetary-accounts/:id/edit"
              element={
                <PrivateRoute requiredPermission="accounting.access_accounting">
                  <MonetaryAccountFormPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/monetary-accounts/:id"
              element={
                <PrivateRoute requiredPermission="accounting.access_accounting">
                  <MonetaryAccountDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/products/create"
              element={
                <PrivateRoute requiredPermission="products.access_products">
                  <ProductFormPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/products/:id/edit"
              element={
                <PrivateRoute requiredPermission="products.access_products">
                  <ProductFormPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/client-quotes"
              element={
                <PrivateRoute requiredPermission="quotes.access_quotes">
                  <ClientQuoteListPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/client-quotes/create"
              element={
                <PrivateRoute requiredPermission="quotes.access_quotes">
                  <ClientQuoteFormPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/client-quotes/:id/edit"
              element={
                <PrivateRoute requiredPermission="quotes.access_quotes">
                  <ClientQuoteFormPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sales"
              element={
                <PrivateRoute requiredPermission="sales.access_sales">
                  <SalesListPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/sales/:id"
              element={
                <PrivateRoute requiredPermission="sales.access_sales">
                  <SalesDetailPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/clients"
              element={
                <PrivateRoute requiredPermission="clients.access_clients">
                  <ClientsListPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/clients/create"
              element={
                <PrivateRoute requiredPermission="clients.access_clients">
                  <ClientFormPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/clients/:id/edit"
              element={
                <PrivateRoute requiredPermission="clients.access_clients">
                  <ClientFormPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/suppliers"
              element={
                <PrivateRoute requiredPermission="suppliers.access_suppliers">
                  <SuppliersListPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/suppliers/create"
              element={
                <PrivateRoute requiredPermission="suppliers.access_suppliers">
                  <SupplierFormPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/suppliers/:id/edit"
              element={
                <PrivateRoute requiredPermission="suppliers.access_suppliers">
                  <SupplierFormPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/purchases"
              element={
                <PrivateRoute requiredPermission="purchases.access_purchases">
                  <PurchaseOrdersListPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/purchases/create"
              element={
                <PrivateRoute requiredPermission="purchases.access_purchases">
                  <PurchaseOrderFormPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/purchases/:id/edit"
              element={
                <PrivateRoute requiredPermission="purchases.access_purchases">
                  <PurchaseOrderFormPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/purchases/:id"
              element={
                <PrivateRoute requiredPermission="purchases.access_purchases">
                  <PurchaseOrderDetailPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/payroll"
              element={
                <PrivateRoute requiredPermission="payroll.access_payroll">
                  <PayrollListPage />
                </PrivateRoute>
              }
            />

            {/* /payroll/create must precede /payroll/:id or "create" is
                captured as an id. */}
            <Route
              path="/payroll/create"
              element={
                <PrivateRoute requiredPermission="payroll.access_payroll">
                  <PayrollFormPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/payroll/:id/edit"
              element={
                <PrivateRoute requiredPermission="payroll.access_payroll">
                  <PayrollFormPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/payroll/:id"
              element={
                <PrivateRoute requiredPermission="payroll.access_payroll">
                  <PayrollDetailPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/employees"
              element={
                <PrivateRoute requiredPermission="employees.access_employees">
                  <EmployeesListPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/employees/create"
              element={
                <PrivateRoute requiredPermission="employees.access_employees">
                  <EmployeeFormPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/employees/:id/edit"
              element={
                <PrivateRoute requiredPermission="employees.access_employees">
                  <EmployeeFormPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/employees/:id"
              element={
                <PrivateRoute requiredPermission="employees.access_employees">
                  <EmployeeDetailPage />
                </PrivateRoute>
              }
            />

            <Route path="/" element={<Navigate to="/dashboard" />} />

            {/* Catch-all. Without it an undefined URL matched no route and
                React Router rendered an empty document — a blank page, with
                no navigation to escape from when signed out. */}
            <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </AppLayout>
        </AuthProvider>
      </NotificationProvider>
    </BrowserRouter>
  );
}

export default App;
